import { useState } from 'react'
import { ShieldAlert, ShieldCheck, Undo2 } from 'lucide-react'
import { ConfirmDialog, EmptyState, ErrorState, PageHeader, StatusBadge } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useSodConflicts, useSodOverrides, useSodOverrideMutations } from '../api'
import type { OverrideView } from '../types'

/**
 * The permission pairs nobody may hold together, and every exception that has been granted.
 *
 * <p>Read-only for the rules: they are defined in code, not configuration, because a
 * segregation rule an administrator can switch off is not a control — it is a setting. What
 * an administrator can do is record a named, reasoned, dated exception for one person, and
 * that needs a higher permission than handing out the grants it excuses.
 *
 * <p>Expired and revoked exceptions stay on the list, greyed. "Who was allowed to do both,
 * and who said so" is a question an auditor asks about last year, and a deleted row cannot
 * answer it.
 */
export function SegregationOfDutiesPage() {
  useBreadcrumb()
  const conflicts = useSodConflicts()
  const overrides = useSodOverrides()
  const { revoke } = useSodOverrideMutations()

  const [toRevoke, setToRevoke] = useState<OverrideView | null>(null)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Segregation of duties"
        subtitle="Combinations of permission that one person may not hold, and the exceptions on record."
      />

      <Card className="p-5">
        <h2 className="mb-1 flex items-center gap-2 text-base font-semibold">
          <ShieldCheck className="size-4" aria-hidden="true" />
          The rules
        </h2>
        <p className="mb-3 text-sm text-slate-600">
          Defined in code and enforced when a grant is made — a rule that could be switched
          off here would be a setting, not a control. Granting a role that would breach one of
          these is refused, and the entity-grants screen says which pair it was.
        </p>
        {conflicts.isPending && <Skeleton className="h-20" />}
        {conflicts.isError && (
          <ErrorState error={conflicts.error} onRetry={() => void conflicts.refetch()} />
        )}
        {conflicts.isSuccess && conflicts.data.length === 0 && (
          <p className="text-sm text-slate-600">No segregation rules are defined.</p>
        )}
        {conflicts.isSuccess && conflicts.data.length > 0 && (
          <ul className="space-y-1" data-testid="sod-rules">
            {conflicts.data.map((rule) => (
              <li key={rule} className="flex items-start gap-2 text-sm">
                <ShieldAlert className="mt-0.5 size-4 shrink-0 text-slate-400" aria-hidden="true" />
                <code>{rule}</code>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <div className="border-b border-slate-200 p-4">
          <h2 className="text-base font-semibold">Recorded exceptions</h2>
          <p className="mt-1 text-sm text-slate-600">
            Recording one needs System Administrator — deliberately a higher permission than
            the one that hands out the grants it excuses. Revoked and expired exceptions stay
            here as a record of what was once allowed.
          </p>
        </div>

        {overrides.isPending && <Skeleton className="m-4 h-24" />}
        {overrides.isError && (
          <ErrorState error={overrides.error} onRetry={() => void overrides.refetch()} />
        )}
        {overrides.isSuccess && overrides.data.length === 0 && (
          <EmptyState
            title="No exceptions have been granted"
            description="Every account satisfies the rules above, which is the state to be in."
          />
        )}
        {overrides.isSuccess && overrides.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Pair allowed</th>
                  <th className="p-3 text-start font-medium">Why</th>
                  <th className="p-3 text-start font-medium">Granted</th>
                  <th className="p-3 text-start font-medium">Until</th>
                  <th className="p-3 text-start font-medium">State</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {overrides.data.map((override) => (
                  <tr
                    key={override.id}
                    className={`border-b border-slate-100 ${override.live ? '' : 'text-slate-400'}`}
                  >
                    <td className="p-3"><code>{override.conflictingPair}</code></td>
                    <td className="p-3">{override.reason}</td>
                    <td className="p-3">{override.approvedAt.slice(0, 10)}</td>
                    <td className="p-3">{override.expiresAt?.slice(0, 10) ?? 'no end date'}</td>
                    <td className="p-3">
                      <StatusBadge
                        status={override.live ? 'LIVE' : 'ENDED'}
                        tone={override.live ? 'warning' : 'neutral'}
                        label={override.revokedAt ? 'Revoked' : override.live ? 'In force' : 'Expired'}
                      />
                    </td>
                    <td className="p-3 text-end">
                      {override.live && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setToRevoke(override)}
                        >
                          <Undo2 className="size-4" aria-hidden="true" />
                          Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={toRevoke != null}
        onOpenChange={(open) => { if (!open) setToRevoke(null) }}
        title="Revoke this exception?"
        description={
          toRevoke
            ? `The rule ${toRevoke.conflictingPair} applies to that account again from now. The exception stays on the list as a record that it was once allowed.`
            : ''
        }
        confirmLabel="Revoke"
        onConfirm={() => {
          if (toRevoke) revoke.mutate(toRevoke.id)
          setToRevoke(null)
        }}
      />
    </div>
  )
}
